import * as THREE from 'three'
import { EditorUserError } from '@glass-designer/utils/editorErrors'

import Config from '@glass-designer/config'
import BaseTool from '@glass-designer/tools/baseTool'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'

import GlassDesignerTypes from '@glass-designer/types'


// Dimension Edit Datum tool
// Tool used when dimension editing & user selects 'change datum'
// Currently only used for center cutout/hole dimension offsets
export default class DimensionEditDatumTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.dimensionEditDatumTool
    this.selectedShape = null
    this.selectedHole = null
    this.selectedDatumLink = null
    this.datumLinks = []
    this.datumLinkUpdateIndex = 0
    this.anchorWorldPoint = null
    this.selectedDimension = null

    this.selectionPoint = new THREE.Vector2(0, 0)
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShape = this.editor.getObjectById(options.shapeId)
    this.selectedHole = this.editor.getObjectById(options.holeId)
    this.selectedDatumLink = this.selectedHole.getDatumLinkById(options.datumLinkId)
    this.datumLinks = []
    this.datumLinkUpdateIndex = 0

    // Hole Offset Editing
    this.anchorWorldPoint = this.selectedHole.calculateAnchorWorldPoint()

    this.datumLinkUpdateIndex = this.selectedHole.datumLinks.findIndex(link => link.id == this.selectedDatumLink.id)
    if(this.datumLinkUpdateIndex != -1) {
      this.datumLinks = this.selectedHole.datumLinks.map(link => link.clone())

      // Find dimension using data
      this.selectedDimension = this.editor.getDimensionHelper().findDimensionByLinkData(GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension, {
        shapeId: this.selectedShape.id,
        datumLinkId: this.selectedDatumLink.id,
        holeId: this.selectedHole.id
      })

      var displayOptions = {
        horizontalPosition: this._calculatePopoverHorizontalPosition()
      }

      this.editor.component.displayToolSettingsPopover({
        title: 'Change Datum',
        icon: 'fa-bullseye-pointer',
        actionButtons: [],
        infoText: 'Select new point for datum line\nAll datum lines must intersect at a single point to be valid',
        context: this,
        cancelCallback: (ctx, userCancelled) => {
          if(userCancelled)
            ctx.closeTool()
        },
        actionCallback: (ctx, action) => {
        }
      }, displayOptions)
    }
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    if(this.selectedShape) {
      // Force update of dimensions on the shape
      this.editor.updateShapeDimensions(this.selectedShape)
    }

    this.selectedShape = null
    this.selectedHole = null
    this.selectedDatumLink = null
    this.datumLinks = []

    // Cancel any editing
    this.editor.component.hidePopover()
  }


   // onMouseMoveEvent
   onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    // Find closest point on shape segments / holes
    if(this.selectedShape && this.selectedHole) {
      this.selectionPoint.set(mouseData.position.x, mouseData.position.y)

      let isDatumSourceValid = this._updateHoleDatumSourceFromPoint(this.selectionPoint)
      var datumLink = this.datumLinks[this.datumLinkUpdateIndex]
        
      // Update dimension
      var dirToHole = datumLink.calculateLinkDirection(this.editor)
      if(dirToHole == null)
        return

      var dirRight = new THREE.Vector2(dirToHole.y, -dirToHole.x)
      var holeEdge = this.anchorWorldPoint

      var datumOrigin = holeEdge.clone().addScaledVector(dirToHole, -Math.abs(datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))

      // Directly modify the dimension to show updates
      if(this.selectedDimension) {
        this.selectedDimension.setErrorFlag(!isDatumSourceValid)

        this.selectedDimension.clearPoints()
        this.selectedDimension.lineDirection = new THREE.Vector3(dirToHole.x, dirToHole.y, 0)
        this.selectedDimension.extendDirection = new THREE.Vector3(dirRight.x, dirRight.y, 0)
        this.selectedDimension.addPoint(new THREE.Vector3(datumOrigin.x, datumOrigin.y, 0))
        this.selectedDimension.addPoint(new THREE.Vector3(holeEdge.x, holeEdge.y, 0))
        this.selectedDimension.build()
      }
      
      this.editor.flagRenderRequired()
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(ev.button == Config.Mouse.buttonSelect) {
      if(this.selectedShape && this.selectedHole) {
        this.selectionPoint.set(mouseData.position.x, mouseData.position.y)

        let isDatumSourceValid = this._updateHoleDatumSourceFromPoint(this.selectionPoint)
        if(!isDatumSourceValid) {
          this.editor.component.errorMessage('Datum source point is not valid', 'Both datum links need to intersect at a point to be valid')

          this.editor.flagRenderRequired()
          this.closeTool()
          return
        }

        // Apply to the hole via undo system
        this.editor.startUndoGroup('Adjusting shape dimension')
        this.editor.addUndoShape(this.selectedShape)
        this.selectedHole.setDatumLinks(this.datumLinks)
        this.editor.closeCurrentUndoGroup()

        // Rebuild dimensions
        this.editor.updateShapeDimensions(this.selectedShape)
        
        this.closeTool()

        return true
      }
    }
  }


  /**
   * Update the selected datum's source info given a point on the shape
   */
  _updateHoleDatumSourceFromPoint(point) {
    if(this.datumLinks.length == 0 || this.datumLinkUpdateIndex == -1)
      return false

    let targetInfo = this.selectedShape.calculateClosestEdgePoint(point, {
      holeRefPointOnly: true,
      ignoreSplitLines: true,
      ignoreHoleId: this.selectedHole.id
    })

    // Circle/Oval shape have different rules for datum links
    let isShapeCircle = this.selectedShape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.circle
    let isShapeOval = this.selectedShape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.oval

    let holeAnchorWorldPoint = this.selectedHole.calculateAnchorWorldPoint()

    if(targetInfo) {
      // Segment Source
      if(targetInfo.segmentId) {
        let segmentIndex = this.selectedShape.findSegmentIndexById(targetInfo.segmentId)
        let segment = this.selectedShape.getSegmentForIndex(segmentIndex)

        let segmentPoint = segment.projectPointOnSegment(holeAnchorWorldPoint)

        var nearObjectInfo = {
          sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegment,
          segmentIndex: segmentIndex,
          closestPoint: segmentPoint,
          distance: segmentPoint.distanceTo(holeAnchorWorldPoint)
        }

        // If the shape is a circle or oval, use bounds edge
        if(isShapeCircle || isShapeOval) {
          let bounds = this.selectedShape.calculateSegmentBounds()
          let closestEdgeIndex = -1
          let closestEdgeDistance = 0
          let closestEdgePoint = null
          let edgeProjectedPoint = null

          // 4 edges: top, right, bottom, left
          let edges = [
            [new THREE.Vector2(bounds.min.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.min.y)],
            [new THREE.Vector2(bounds.max.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.max.y)],
            [new THREE.Vector2(bounds.max.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.max.y)],
            [new THREE.Vector2(bounds.min.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.min.y)]
          ]

          edges.forEach((edge, edgeIndex) => {
            let edgeClosestPoint = MathsUtils.getClosest2DPointOnLineSegment(edge[0], edge[1], point, false)
            let pointToEdgeDistance = edgeClosestPoint.distanceTo(point)

            // Ignore if point is outside of the line
            if(!MathsUtils.point2DOnLineSegment(edge[0], edge[1], edgeClosestPoint, 1.0))
              return

            if(pointToEdgeDistance < closestEdgeDistance || closestEdgeIndex == -1) {
              closestEdgeIndex = edgeIndex
              closestEdgeDistance = pointToEdgeDistance

              edgeProjectedPoint = MathsUtils.getClosest2DPointOnLineSegment(edge[0], edge[1], holeAnchorWorldPoint, false)
            }
          })


          if(closestEdgeIndex != -1) {
            nearObjectInfo = {
              sourceDatumType: GlassDesignerTypes.DatumTypes.shapeBoundsEdge,
              shapeBoundsEdgeIndex: closestEdgeIndex,
              distance: edgeProjectedPoint.distanceTo(holeAnchorWorldPoint),
              closestPoint: edgeProjectedPoint,
            }
          }
        }

        EditorDatumUtils.setDatumLinkFromNearObjectItem(this.datumLinks[this.datumLinkUpdateIndex], this.selectedShape, holeAnchorWorldPoint, nearObjectInfo)

        // Check if the datum links provide a valid solution
        // If not, cannot use this location & flag invalid
        let result = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks)
        if(result)
          return true
      }

      // Datum Line Source
      if(targetInfo.datumLineId) {
        let datumLine = this.selectedShape.getDatumLineById(targetInfo.datumLineId)
        if(!datumLine)
          return false

        let datumPoint = datumLine.projectPointOnDatumLine(holeAnchorWorldPoint)

        let nearObjectInfo = {
          sourceDatumType: GlassDesignerTypes.DatumTypes.datumLine,
          datumLineId: datumLine.id,
          closestPoint: datumPoint,
          distance: datumPoint.distanceTo(holeAnchorWorldPoint)
        }

        EditorDatumUtils.setDatumLinkFromNearObjectItem(this.datumLinks[this.datumLinkUpdateIndex], this.selectedShape, holeAnchorWorldPoint, nearObjectInfo)

        // Check if the datum links provide a valid solution
        // If not, cannot use this location & flag invalid
        let result = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks)
        if(result)
          return true
        
        // Check datum line start or end (whatever is closer)
        let distanceToDatumStart = MathsUtils.calculateDistanceToPlane2D(datumLine.getDirection(), datumLine.startPosition, point)
        let distanceToDatumEnd = MathsUtils.calculateDistanceToPlane2D(datumLine.getDirection().negate(), datumLine.endPosition, point)

        if(distanceToDatumStart < distanceToDatumEnd) {
          datumPoint = MathsUtils.project2DPointOnPlane(holeAnchorWorldPoint, datumLine.getDirection(), datumLine.startPosition)

          nearObjectInfo = {
            sourceDatumType: GlassDesignerTypes.DatumTypes.datumLineStart,
            datumLineId: datumLine.id,
            closestPoint: datumPoint,
            distance: datumPoint.distanceTo(holeAnchorWorldPoint)
          }
        } else {
          datumPoint = MathsUtils.project2DPointOnPlane(holeAnchorWorldPoint, datumLine.getDirection().negate(), datumLine.endPosition)

          nearObjectInfo = {
            sourceDatumType: GlassDesignerTypes.DatumTypes.datumLineEnd,
            datumLineId: datumLine.id,
            closestPoint: datumPoint,
            distance: datumPoint.distanceTo(holeAnchorWorldPoint)
          }
        }

        EditorDatumUtils.setDatumLinkFromNearObjectItem(this.datumLinks[this.datumLinkUpdateIndex], this.selectedShape, holeAnchorWorldPoint, nearObjectInfo)

        // Check if the datum links provide a valid solution
        // If not, cannot use this location & flag invalid
        result = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks)
        if(result)
          return true
      }


      // Hole Source
      if(targetInfo.holeId) {
        let otherHole = this.selectedShape.getHoleById(targetInfo.holeId)
        if(!otherHole)
          return false

        let isHorizontal = Math.abs(targetInfo.point.x - holeAnchorWorldPoint.x) > Math.abs(targetInfo.point.y - holeAnchorWorldPoint.y)

        let nearObjectInfo = {
          sourceDatumType: GlassDesignerTypes.DatumTypes.hole,
          holeId: otherHole.id,
          isVertical: !isHorizontal,
          isHorizontal: isHorizontal,
          closestPoint: targetInfo.point,
          sourceHoleMeasurePointHorzType: targetInfo.holeHorzType,
          sourceHoleMeasurePointVertType: targetInfo.holeVertType,
          sourceHoleMeasureAxisType: isHorizontal ? GlassDesignerTypes.AxisTypes.horizontal : GlassDesignerTypes.AxisTypes.vertical
        }

        EditorDatumUtils.setDatumLinkFromNearObjectItem(this.datumLinks[this.datumLinkUpdateIndex], this.selectedShape, holeAnchorWorldPoint, nearObjectInfo)

        // Check if the datum links provide a valid solution
        // If not, cannot use this location & flag invalid
        let result = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks)
        if(result)
          return true
      }

    }

    return false
  }
}
